import { FunctionComponent, useCallback, useEffect, useState } from 'react';

import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { DestinationModel, QueryModel } from 'models';
import { DCMStudyInstanceModel } from 'models/DCMStudyInstanceModel';

import { apiClient } from 'core/api/globals';
import { FieldContainer, createStyledRhfForm } from 'core/forms';
import { useEvent } from 'core/hooks';
import { NotificationsService } from 'core/notifications';
import { Button, Card, DataTable, DropdownField, GridColumn, HeaderCell, InputField, Page, PageHeader, TextCell } from 'core/ui';
import { DatePickerField } from 'core/ui/DatePicker/DatePickerField';
import { hasText } from 'core/utils';

const StyledForm = createStyledRhfForm('');

export const QueryRetrieveHome: FunctionComponent = () => {
  const [pacsData, setPACSData] = useState<DCMStudyInstanceModel[]>([]);
  const [pacsDataTotal, setPACSDataTotal] = useState(0);
  const [dataState, setDataState] = useState({
    skip: 0,
    take: 40,
  });
  const [selectedExams, setSelectedExams] = useState<Record<string, boolean>>({});
  const [destinationId, setDestinationId] = useState<number>(0);
  const [importing, setImporting] = useState<boolean>(false);
  const [Archives, setArchives] = useState<DestinationModel[]>([]);

  const hasSelectedExams = Object.keys(selectedExams).filter((key) => selectedExams[key]).length > 0;
  const rhfContext = useForm({});
  const {
    reset,
    formState: { isSubmitting },
  } = rhfContext;

  const initialize = useEvent(async () => {
    const remoteArchives = await apiClient.destinationClient.listByIntegration('Query');
    setArchives(remoteArchives);
  });

  useEffect(() => {
    initialize();
  }, [initialize]);

  const handleSubmit: SubmitHandler<QueryModel> = useCallback(
    async (values: QueryModel, event) => {
      event?.preventDefault();
      console.log('submit', values, event);

      //add wildcard search
      if (hasText(values.patientName) && !values.patientName.includes('*')) {
        values.patientName = values.patientName + '*';
      }
      values.destinationId = values.destination?.id ?? 0;
      setDestinationId(values.destinationId);

      //format dob and studydate
      if (values.DOB) {
        values.DOB = formatDate(new Date(values.DOB));
      }
      if (values.studyDate) {
        values.studyDate = formatDate(new Date(values.studyDate));
      } else {
        //default to last 30 days
        const startDate = new Date().setDate(new Date().getDate() - 30);
        const endDate = new Date().setDate(new Date().getDate() + 1);
        const dateRange = formatDate(new Date(startDate)) + '-' + formatDate(new Date(endDate));
        values.studyDate = dateRange;
      }

      const queryResult = await apiClient.dicomClient.query(values);
      console.log('submit results', queryResult);

      setPACSData(queryResult.results || []);
      setPACSDataTotal(queryResult.results?.length || 0);
    },
    [reset],
  );

  function formatDate(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }

  const importExams = useEvent(async () => {
    const importExams = Object.keys(selectedExams).filter((key) => selectedExams[key]);
    NotificationsService.displaySuccess(`Importing Exams`);
    setImporting(true);
    const importResult = await apiClient.dicomClient.import(destinationId, importExams);
    NotificationsService.displaySuccess(`Exams Imported Successfully`);
    setImporting(false);
    setSelectedExams({});
    //navigate back
  });

  return (
    <Page>
      <PageHeader showSessionLocation title="Query Retrieve" />
      <Card>
        <FormProvider {...rhfContext}>
          <StyledForm autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck="false" noValidate onSubmit={rhfContext.handleSubmit(handleSubmit)}>
            <StyledFormContainer>
              <FieldContainer>
                <InputField name="patientName" label="Patient Name" />
              </FieldContainer>
              <FieldContainer>
                <InputField name="patientID" label="Patient MRN" />
              </FieldContainer>
              <FieldContainer>
                <DatePickerField name="DOB" label="Patient DOB" />
              </FieldContainer>
              <FieldContainer>
                <InputField name="accession" label="Accession" />
              </FieldContainer>
              <FieldContainer>
                <InputField name="studyDescription" label="Description" />
              </FieldContainer>
              <FieldContainer>
                <DatePickerField name="studyDate" label="Study Date" />
              </FieldContainer>

              <FieldContainer>
                <DropdownField name="destination" label="Remote Archive" filterable={false} data={Archives} />
              </FieldContainer>
            </StyledFormContainer>
            <div>
              <Button disabled={isSubmitting} type="submit">
                Search
              </Button>
            </div>
          </StyledForm>
        </FormProvider>

        <StyledDataTable
          data={pacsData}
          //filterable
          total={pacsDataTotal}
          selectable
          selectedState={selectedExams}
          onSelectionChange={setSelectedExams}
          dataItemKey={'suid'}
          {...dataState}
        >
          <GridColumn title="Accession" field="accession" filter="text" headerCell={HeaderCell} cell={TextCell} width={100} />
          <GridColumn title="MRN" field="mrn" filter="text" headerCell={HeaderCell} cell={TextCell} />
          <GridColumn title="SUID" field="suid" filter="text" headerCell={HeaderCell} cell={TextCell} />
          <GridColumn title="Study Date" field="studyDate" filter="text" headerCell={HeaderCell} cell={TextCell} />
          <GridColumn title="Birth Date" field="birthDate" filter="text" headerCell={HeaderCell} cell={TextCell} />
          <GridColumn title="Patient Name" field="patientName" filter="text" headerCell={HeaderCell} cell={TextCell} />
          <GridColumn title="Study Description" field="studyDescription" filter="text" headerCell={HeaderCell} cell={TextCell} />
        </StyledDataTable>
        <br />
        <FieldContainer>
          <Button disabled={!hasSelectedExams || importing} type="submit" onClick={importExams}>
            Import Selected Exams
          </Button>
        </FieldContainer>
      </Card>
    </Page>
  );
};

QueryRetrieveHome.displayName = 'Query Retrieve';

const StyledDataTable = styled(DataTable)`
  max-height: 50vh;
  overflow: auto;
`;

const StyledFormContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1em;

  > * {
    min-width: 200px;
  }
`;
